import React from "react";
import { useAuth } from "#app/lib/auth-context";
import { AnalyticsClient, type TrackEvent } from "./analytics-client";

interface AnalyticsContextValue {
  track: (event: TrackEvent) => void;
}

const AnalyticsContext = React.createContext<AnalyticsContextValue | null>(null);

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const { getAccessToken } = useAuth();

  const clientRef = React.useRef<AnalyticsClient | null>(null);

  if (!clientRef.current) {
    clientRef.current = new AnalyticsClient(getAccessToken);
  }

  // Start/stop flush timer on mount/unmount, flush when app backgrounds
  React.useEffect(() => {
    const client = clientRef.current!;
    client.start();

    // Drain the queue across tab visibility changes. visibilitychange fires
    // while the page is still alive, so a normal async flush() can refresh the
    // auth token. pagehide is the last-chance keepalive fallback for events
    // queued between visibility transitions.
    let handleVisibility: (() => void) | null = null;
    let handlePageHide: (() => void) | null = null;
    if (typeof window !== "undefined") {
      handleVisibility = () => {
        if (document.visibilityState === "hidden") client.flush();
      };
      handlePageHide = () => client.flushBeacon();
      document.addEventListener("visibilitychange", handleVisibility);
      window.addEventListener("pagehide", handlePageHide);
    }

    return () => {
      if (handleVisibility) {
        document.removeEventListener("visibilitychange", handleVisibility);
      }
      if (handlePageHide) {
        window.removeEventListener("pagehide", handlePageHide);
      }
      client.stop();
    };
  }, []);

  const track = React.useCallback((event: TrackEvent) => {
    clientRef.current?.track(event);
  }, []);

  const value = React.useMemo(() => ({ track }), [track]);

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

export function useAnalytics(): AnalyticsContextValue {
  const context = React.useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
}
